import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CaseIdSchema, PeriodSchema, type Period } from '@casetrace/shared';
import { collection, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';

export const periodsQueryKey = (caseId: string) => ['periods', caseId] as const;

function parsePeriods(docs: Array<{ id: string; data: () => unknown }>): Period[] {
  const items: Period[] = [];
  for (const doc of docs) {
    const parsed = PeriodSchema.safeParse({
      ...(doc.data() as Record<string, unknown>),
      id: doc.id,
    });
    if (parsed.success) {
      items.push(parsed.data);
    }
  }
  return items;
}

async function fetchPeriods(caseId: string): Promise<Period[]> {
  const parsedCaseId = CaseIdSchema.parse(caseId);
  const ref = collection(db, 'cases', parsedCaseId, 'periods');
  const snap = await getDocs(ref);
  return parsePeriods(snap.docs);
}

/**
 * Realtime-abonnement på tidsperioder for en sak (historievisning m.m.).
 */
export function useCasePeriods(caseId: string) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: periodsQueryKey(caseId),
    queryFn: () => fetchPeriods(caseId),
    enabled: Boolean(caseId),
  });

  useEffect(() => {
    if (!caseId) return;
    const parsedCaseId = CaseIdSchema.parse(caseId);
    const ref = collection(db, 'cases', parsedCaseId, 'periods');
    return onSnapshot(ref, (snap) => {
      queryClient.setQueryData(periodsQueryKey(caseId), parsePeriods(snap.docs));
    });
  }, [caseId, queryClient]);

  return query;
}
